// The one way to move a Bitmovin player to a time. Presenter mode's "seek to
// my position" (channels/presentation_channel.js) and the time links of
// controllers/video_chapters_controller.js both go through seekPlayer, so a
// fix to how a seek waits for the player lands in both.
//
// `window.players` is the registry modules/_players.js defines and
// shared/_player.html.erb fills, keyed by the player element's id.

// The player registered for a `.video-player` (or `.bitmovinplayer-container`)
// element, or null.
export function playerFor(element) {
  if (!element || !window.players) return null;
  return window.players[element.id] || null;
}

// The player inside a presentation widget, the way presenter mode finds it.
export function playerInWidget(root, widgetId) {
  return playerFor(
    root.querySelector(`[data-widget-id="${widgetId}"] .bitmovinplayer-container`)
  );
}

// Seek, optionally play. A player still behind its poster is loaded first,
// like clicking the poster does, and the seek waits for Ready: Bitmovin drops
// a seek made before it.
export function seekPlayer(player, seconds, { play = false } = {}) {
  if (!player || !Number.isFinite(seconds)) return;

  whenReady(player, () => {
    player.seek(seconds);
    if (play) {
      const started = player.play();
      if (started && started.catch) started.catch(() => {});
    }
  });
}

// "1:02:03", "02:03", "123", "#t=02:03" or "t=02:03". null when it is not a time.
export function parseTime(text) {
  const value = String(text || "").replace(/^#?t=/, "").trim();
  if (!/^\d+(:\d{1,2}){0,2}(\.\d+)?$/.test(value)) return null;

  return value.split(":").reduce((total, part) => total * 60 + parseFloat(part), 0);
}

// The `t=` part of a hash such as "#t=03:12" or "#chapter-2&t=03:12", in
// seconds, or null.
export function timeFromHash(hash) {
  const match = /[#&]t=([^&]+)/.exec(hash || "");
  return match ? parseTime(decodeURIComponent(match[1])) : null;
}

// modules/_players.js sets isReady in handlePlayerReady.
function whenReady(player, callback) {
  const config = player.getConfig()._config || {};
  if (config.isReady) return callback();

  const ready = window.bitmovin?.player?.PlayerEvent?.Ready || "ready";
  const once = () => {
    player.off(ready, once);
    callback();
  };
  player.on(ready, once);

  if (window.players && window.players.load) window.players.load(player);
}
